<template>
    <div
        class="input-range-text-inputs"
        data-qa="input-range-text-inputs"
        :class="classes"
    >
        <div
            class="min-max"
            data-qa="minmax"
        >
            <div
                class="min"
                data-qa="min"
            >
                {{ valueFormatter(minFinal, 0) }}
            </div>

            <div
                class="max"
                data-qa="max"
            >
                {{ valueFormatter(maxFinal, 1) }}
            </div>
        </div>

        <div
            class="text-inputs"
            data-qa="inputs"
        >
            <input
                :key="valueMinUiText"
                v-model.lazy="valueMinUiText"
                v-qa="'min'"
                class="text-input min"
                type="text"
                :aria-label="String(minLabel)"
                :disabled
            />

            <input
                :key="valueMaxUiText"
                v-model.lazy="valueMaxUiText"
                v-qa="'max'"
                class="text-input max"
                type="text"
                :aria-label="String(maxLabel)"
                :disabled
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, type Ref, type WritableComputedRef } from 'vue'

import useInputRange from './useInputRange'
import {
    defaults,
    type InputRangeModel,
    type InputRangeTwoKnobbedIndex,
    type InputRangeTwoKnobbedValueFormatter,
    type Props,
} from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import { clamp as clampFunc } from '@/helpers'
import { useInputRangeModels } from '@/components/ui/inputs/range/useInputRangeModel.ts'

const props = withDefaults(defineProps<Props>(), defaults)

const emit = defineEmits<{
    'update:modelValue': [InputRangeModel]
}>()

const { minFinal, maxFinal } = useInputRange(props)

const { min, max } = useInputRangeModels(
    () => props.modelValue,
    minFinal,
    maxFinal,
    (model: InputRangeModel) => {
        emit('update:modelValue', model)
    },
    true
)

const classes = computed(() => ({
    disabled: props.disabled,
}))

function useTextModel(
    value: Ref<number>,
    bound: Ref<number>,
    formatter: InputRangeTwoKnobbedValueFormatter,
    index: InputRangeTwoKnobbedIndex
): WritableComputedRef<string, string> {
    return computed({
        get() {
            return formatter(value.value, index)
        },
        set(val: string) {
            value.value = clampFunc(
                parseNumber(val, bound.value),
                minFinal.value,
                maxFinal.value
            )
        },
    })
}

function parseNumber(val: string, defaultVal: number): number {
    const clean = val.replace(/[^0-9.-]+/g, '')

    if (!clean) {
        return defaultVal
    }

    const asNum = Number(clean)

    if (isNaN(asNum)) {
        return defaultVal
    }

    return asNum
}

const valueMinUiText = useTextModel(min, minFinal, props.valueFormatter, 0)
const valueMaxUiText = useTextModel(max, maxFinal, props.valueFormatter, 1)
</script>

<style lang="scss" scoped>
.input-range-text-inputs {
    &.disabled {
        cursor: not-allowed !important;
    }

    .min-max,
    .text-inputs {
        //position: absolute;
        width: 100%;
        display: flex;
        justify-content: space-between;

        font-family: $effra-font-stack;
        font-weight: 400;
        font-size: 0.875rem;
        line-height: 0.875rem;
        text-align: center;
        letter-spacing: 0.05em;
        text-transform: uppercase;
    }

    .min-max {
        margin-bottom: 18px;
    }

    .text-inputs {
        .text-input {
            width: 80px;
            height: 40px;
            border: 1px solid $grey-200;
            border-radius: 12px;
        }

        input[type='number'] {
            -moz-appearance: textfield;
            appearance: textfield;
            margin: 0;
        }
    }
}
</style>
